import React, { useState, useEffect } from "react";
import NavHeader from "../components/nav/NavHeader";
import SideMenu from "../components/nav/Sidemenu";
import ToolRoleManagementTable from "../components/pageManagement/ToolRoleManagementTable";
import ToolRoleRegistrationsForm from "../components/pageManagement/ToolRoleRegistrationsForm";
import AssignPagesModal from "../components/pageManagement/AssignPagesModal";
import "../assets/css/reportRegistrations.css";
import PageHeader from "../components/layout/PageHeader";
import TabStrip from "../components/layout/TabStrip";
import RankedTable from "../components/layout/RankedTable";
import SingleFieldEditModal from "../components/layout/SingleFieldEditModal";
import ConfirmDialog from "../components/layout/ConfirmDialog";
import ToastNotification from "../components/layout/ToastNotification";
import AppButton from "../components/layout/AppButton";
import { useToolRoleAdmin } from "../hooks/useToolRoleAdmin";
import { FaSitemap, FaPlusCircle } from "react-icons/fa";

const PageManagement = () => {
  const {
    fetchPagesCatalog,
    fetchToolRoles: fetchRawToolRoles,
    createToolRole: createToolRoleApi,
    updateToolRole: updateToolRoleApi,
    deleteToolRole: deleteToolRoleApi,
    fetchMappedPages,
    savePageMapping,
  } = useToolRoleAdmin();

  const [activeTab, setActiveTab] = useState("management");
  const [searchText, setSearchText] = useState("");
  const [roles, setRoles] = useState([]);
  const [pagesCatalog, setPagesCatalog] = useState([]);
  const [tableLoading, setTableLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editRoleData, setEditRoleData] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, roleId: null });

  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedRoleForAssign, setSelectedRoleForAssign] = useState(null);
  const [selectedPageKeys, setSelectedPageKeys] = useState([]);
  const [featureToggles, setFeatureToggles] = useState({});
  const [loadingMappedPages, setLoadingMappedPages] = useState(false);
  const [savingMapping, setSavingMapping] = useState(false);

  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const showToast = (message, severity = "success") => setToast({ open: true, message, severity });
  const handleCloseToast = () => setToast((t) => ({ ...t, open: false }));

  const formatDateFromISO = (isoString) => {
    if (!isoString) return "";
    const date = new Date(isoString);
    const pad = (v) => String(v).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  };

  const loadRoles = async () => {
    setTableLoading(true);
    try {
      const data = await fetchRawToolRoles();
      const raw = Array.isArray(data) ? data : [];
      setRoles(raw.map((r) => ({
        id: r.toolRoleId,
        name: r.roleName,
        createdBy: r.createdBy || "",
        createdAt: formatDateFromISO(r.createdAt),
      })));
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
    fetchPagesCatalog().then(setPagesCatalog).catch(() => setPagesCatalog([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRoles = roles.filter((r) => r.name.toLowerCase().includes(searchText.toLowerCase()));

  // ── create ────────────────────────────────────────────────────────────────
  const handleCreateRole = async (name) => {
    const isDuplicate = roles.some((r) => r.name.toLowerCase() === name.toLowerCase());
    if (isDuplicate) {
      showToast("A tool role with this name already exists.", "warning");
      return;
    }
    try {
      setCreateLoading(true);
      const result = await createToolRoleApi({ roleName: name });
      await loadRoles();
      showToast(result?.message || "Tool role created successfully");
      setActiveTab("management");
    } catch (err) {
      showToast("Error creating tool role: " + err.message, "error");
    } finally {
      setCreateLoading(false);
    }
  };

  // ── edit ──────────────────────────────────────────────────────────────────
  const handleEditRole = (role) => {
    setEditRoleData({ ...role });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditRoleData(null);
  };

  const handleUpdateRole = async (event) => {
    event.preventDefault();
    if (!editRoleData) return;
    try {
      setUpdateLoading(true);
      const result = await updateToolRoleApi(editRoleData.id, { roleName: editRoleData.name });
      await loadRoles();
      showToast(result?.message || "Tool role updated successfully");
      handleCloseEditModal();
    } catch (err) {
      showToast("Error updating tool role: " + err.message, "error");
    } finally {
      setUpdateLoading(false);
    }
  };

  // ── delete ────────────────────────────────────────────────────────────────
  const handleDeleteRole = (id) => {
    if (!id) { showToast("Invalid tool role ID", "error"); return; }
    setConfirmDialog({ open: true, roleId: id });
  };

  const handleConfirmDeleteRole = async () => {
    const id = confirmDialog.roleId;
    if (!id) return;
    try {
      setDeletingId(id);
      const result = await deleteToolRoleApi(id);
      await loadRoles();
      showToast(result?.message || "Tool role deleted successfully");
      setConfirmDialog({ open: false, roleId: null });
    } catch (err) {
      showToast("Error deleting tool role: " + err.message, "error");
    } finally {
      setDeletingId(null);
    }
  };

  // ── assign pages ──────────────────────────────────────────────────────────
  const handleAssignPages = async (role) => {
    setSelectedRoleForAssign(role);
    setSelectedPageKeys([]);
    // Every feature defaults to disabled until we know otherwise
    setFeatureToggles({});
    setShowAssignModal(true);
    try {
      setLoadingMappedPages(true);
      const { pageKeys, features } = await fetchMappedPages(role.id);
      setSelectedPageKeys(pageKeys);
      setFeatureToggles(features);
    } catch (err) {
      console.error("Error fetching mapped pages:", err);
    } finally {
      setLoadingMappedPages(false);
    }
  };

  const handleCloseAssignModal = () => {
    setShowAssignModal(false);
    setSelectedRoleForAssign(null);
    setSelectedPageKeys([]);
    setFeatureToggles({});
  };

  const handleTogglePage = (pageKey) => {
    setSelectedPageKeys((prev) =>
      prev.includes(pageKey) ? prev.filter((k) => k !== pageKey) : [...prev, pageKey]
    );
  };

  const handleToggleFeature = (label) => {
    setFeatureToggles((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const pageOnlyCatalog = pagesCatalog.filter((p) => (p.type || "page") === "page");
  const handleSelectAllPages = () => setSelectedPageKeys(pageOnlyCatalog.map((p) => p.key));
  const handleClearAllPages = () => setSelectedPageKeys([]);

  const handleSaveAssignment = async (event) => {
    event.preventDefault();
    if (!selectedRoleForAssign) return;
    try {
      setSavingMapping(true);
      await savePageMapping(selectedRoleForAssign.id, selectedPageKeys, featureToggles);
      handleCloseAssignModal();
      showToast("Pages assigned successfully!");
    } catch (err) {
      showToast("Error assigning pages: " + err.message, "error");
    } finally {
      setSavingMapping(false);
    }
  };

  return (
    <div>
      <NavHeader />
      <SideMenu />
      <div style={{ position: "relative" }} id="main">
        <PageHeader
          title="Page Management"
          subtitle="Create tool roles and assign which pages of the tool they can access."
        />

        <div className="pb-4">
          <TabStrip
            tabs={[
              { label: "Tool Role Management", value: "management", icon: <FaSitemap /> },
              { label: "Tool Role Registration", value: "registrations", icon: <FaPlusCircle /> },
            ]}
            active={activeTab}
            onChange={setActiveTab}
          />
        </div>

        {error && (
          <div className="container-fluid pb-4">
            <div className="alert alert-warning alert-dismissible fade show" role="alert">
              <strong>Warning:</strong> {error}
              <AppButton variant="outline" onClick={() => setError(null)} style={{ padding: "2px 10px", float: "right", lineHeight: 1 }} title="Dismiss">
                ×
              </AppButton>
            </div>
          </div>
        )}

        {tableLoading && activeTab === "management" ? (
          <div className="pb-4">
            <RankedTable
              columns={[{ label: "Role Name" }, { label: "Created At" }, { label: "Actions", align: "center" }]}
              rows={Array.from({ length: 6 }, () => ({ _skeleton: true }))}
              emptyText=""
              renderRow={() => (<>
                {[1, 2, 3].map((j) => (
                  <td key={j} className="px-5 py-3">
                    <div style={{ height: "13px", background: "#f1f5f9", borderRadius: "6px", width: j === 3 ? "60%" : "85%", animation: "pulse 1.5s ease-in-out infinite" }} />
                  </td>
                ))}
              </>)}
            />
          </div>
        ) : activeTab === "management" ? (
          <ToolRoleManagementTable
            searchText={searchText}
            setSearchText={setSearchText}
            filteredRoles={filteredRoles}
            onEditRole={handleEditRole}
            onDeleteRole={handleDeleteRole}
            onAssignPages={handleAssignPages}
            deleteLoadingId={deletingId}
          />
        ) : (
          <ToolRoleRegistrationsForm onCreateRole={handleCreateRole} loading={createLoading} />
        )}

        <SingleFieldEditModal
          open={showEditModal && !!editRoleData}
          title="Edit Tool Role"
          label="Tool Role Name"
          value={editRoleData?.name || ""}
          placeholder="Enter tool role name"
          onChange={(e) => setEditRoleData((prev) => ({ ...prev, name: e.target.value }))}
          onSubmit={handleUpdateRole}
          onClose={handleCloseEditModal}
          loading={updateLoading}
        />

        <AssignPagesModal
          open={showAssignModal && !!selectedRoleForAssign}
          roleName={selectedRoleForAssign?.name}
          pages={pagesCatalog}
          loading={loadingMappedPages}
          selectedKeys={selectedPageKeys}
          featureToggles={featureToggles}
          onToggle={handleTogglePage}
          onToggleFeature={handleToggleFeature}
          onSelectAll={handleSelectAllPages}
          onClearAll={handleClearAllPages}
          onSubmit={handleSaveAssignment}
          onClose={handleCloseAssignModal}
          submitting={savingMapping}
        />

        <ConfirmDialog
          open={confirmDialog.open}
          title="Delete Tool Role"
          message="Are you sure you want to delete this tool role? This action cannot be undone."
          confirmLabel="Delete"
          loadingLabel="Deleting..."
          loading={!!deletingId}
          onConfirm={handleConfirmDeleteRole}
          onClose={() => { if (!deletingId) setConfirmDialog({ open: false, roleId: null }); }}
        />
        <ToastNotification
          open={toast.open}
          message={toast.message}
          severity={toast.severity}
          onClose={handleCloseToast}
        />
      </div>
    </div>
  );
};

export default PageManagement;
